import _ from 'lodash';
import loginAsSession from './login-as-session';

// elsa-v3's cashflow route (elsa-v3/src/router: /cashflow-real/:tab?). The home tab is the
// redesign's landing screen; a direct hit on the Vue 2 cashflow keeps the cashflow tab.
const V3_CASHFLOW_PATH = '/v3/cashflow-real';
const V3_HOME_PATH = `${V3_CASHFLOW_PATH}/home`;

// Only these keys follow the customer into v3. An allow-list, because the login flow and
// login-as put the phone number and tokens (accessToken, inviteToken) in the query, and the
// state router forwards the whole query to the home route.
const FORWARDED_QUERY_KEYS = ['openChatbot', 'chatbotMessage', 'promoCode', 'transactionId', 'trendView', 'celebrate', 'hideCta'];

// Vue 2 cashflow deep links v3 has no screen for yet: the customer stays in Vue 2 so the link works.
const LEGACY_DEEP_LINK_KEYS = ['expenses', 'balanceMoneyCalculator', 'openMonth', 'q'];

// The months a cashflow link can name besides the current one; v3 opens them through its month
// navigation (?budgetDate=), with a transaction or trend link beside it. One that also carries
// another deep link (chat, promo, popup flags) stays in Vue 2.
const LINKED_MONTH = /^(previous|\d{4}-\d{2})$/;
// The deep links v3 opens in the linked month; any other one beside a month stays in Vue 2.
const MONTH_DEEP_LINK_KEYS = ['transactionId', 'trendView'];

function linkedMonth({ budgetDate } = {}) {
  return budgetDate && budgetDate !== 'current' ? budgetDate : null;
}

// Route names carry a Web_/Mob_ prefix in the webview tiers, hence endsWith.
function isCashflowAppRoute(routeName) {
  return _.endsWith(routeName, 'CashflowApp');
}

function isForwardedKey(key) {
  return _.startsWith(key, 'utm_') || _.includes(FORWARDED_QUERY_KEYS, key);
}

// vue-router gives repeated keys as arrays; Vue 2's deep links take the first value.
function firstValue(value) {
  return Array.isArray(value) ? value[0] : value;
}

// Why a customer in the flag still gets the Vue 2 cashflow for this navigation, or null.
function legacyReasonFor({ query = {}, params = {} } = {}, { incognito = false, optedOut = false, deepLinkFallback = false } = {}) {
  // Login-as sessions stay in Vue 2 only where the tab can't be marked as one: elsa-v3 would lose
  // the incognito after the first navigation (utils/login-as-session.js).
  if ((incognito || loginAsSession.isIncognitoQuery(query.incognito)) && !loginAsSession.isMarked()) {
    return 'incognito';
  }
  // The customer just used v3's hidden escape hatch to get here (see redesign.js).
  if (optedOut) {
    return 'opt-out';
  }
  const month = linkedMonth(params);
  if (month && (!LINKED_MONTH.test(month) || _.some(_.difference(FORWARDED_QUERY_KEYS, MONTH_DEEP_LINK_KEYS), key => query[key]))) {
    return 'budget-date';
  }
  if (deepLinkFallback || _.some(LEGACY_DEEP_LINK_KEYS, key => query[key])) {
    return 'deep-link';
  }
  return null;
}

// Months are navigated on the cashflow tab only.
function redesignPathFor(routeName, params = {}) {
  return isCashflowAppRoute(routeName) || linkedMonth(params) ? V3_CASHFLOW_PATH : V3_HOME_PATH;
}

function redesignUrlFor(routeName, query = {}, params = {}, { incognito = false } = {}) {
  const path = redesignPathFor(routeName, params);
  const forwarded = _.omitBy({
    ..._.mapValues(_.pickBy(query, (value, key) => isForwardedKey(key)), firstValue),
    budgetDate: linkedMonth(params),
    incognito: incognito || loginAsSession.isIncognitoQuery(query.incognito) ? 'true' : null,
  }, _.isNil);
  const search = new URLSearchParams(forwarded).toString();
  return search ? `${path}?${search}` : path;
}

export default { legacyReasonFor, redesignPathFor, redesignUrlFor };
